import posthog from 'posthog-js'

import { onUntrackedPage } from 'utilities/untracked-pages'
import { isLocalReplayOn, localReplayOverrides } from './posthog-local-replay'
import {
  maskMeetReplayNetwork,
  maskReplayAttribute,
  maskReplayInput,
  maskReplayText,
  protectMeetReplayEventSecretsOnly,
} from './meet-replay-privacy'

const isBrowser = typeof window !== 'undefined'
const isProduction = process.env.APP_ENV === 'production'
// Enabled on staging too so we can verify events/recordings before they ship to
// prod. Requires NEXT_PUBLIC_POSTHOG_KEY to be set in the staging environment.
const isStaging = process.env.APP_ENV === 'staging'

// Force-enable on non-prod by visiting any URL with `?ph_record=1`. Reload
// without the param (or close the tab) to stop. Evaluated once at init.
const isForceRecord = () => {
  if (!isBrowser) return false
  return new URLSearchParams(window.location.search).get('ph_record') === '1'
}

// Local session replay into the Development project, for checking replay
// fidelity on a local /meet call (clients/posthog-local-replay.ts). Never on
// outside a development build; production and staging are unchanged.
const isLocalReplay = isBrowser && isLocalReplayOn()

// Never on an untracked page (utilities/untracked-pages.ts), whatever else.
const shouldEnable =
  isBrowser &&
  !onUntrackedPage() &&
  !!process.env.NEXT_PUBLIC_POSTHOG_KEY &&
  (isProduction || isStaging || isForceRecord() || isLocalReplay)

/**
 * The SDK's session recording options. Local replay uses production's exactly
 * (then any local overrides), so a local recording tests the shipped config.
 */
export const sessionRecordingOptions = ({
  production,
}: {
  production: boolean
}) => ({
  // 10% of sessions in production, as before the Call Guidance pilot.
  // The /meet controller overrides it.
  sampleRate: production ? 0.1 : 1,
  // The project's minimum duration (30 s) counts from when this page's
  // recording started, not from the PostHog session's start, so a short
  // /meet visit after a longer session elsewhere still never uploads.
  strictMinimumDuration: true,
  // Keep rrweb events inspectable by before_send. The SDK otherwise gzips
  // full snapshots before that hook and could hide sensitive DOM strings.
  compress_events: false,
  maskAllInputs: true,
  // On /meet input values are recorded as typed (unredacted under the
  // PostHog BAA), passwords aside; elsewhere rrweb's asterisks, as before.
  maskInputFn: maskReplayInput,
  // Page text is recorded everywhere; on /meet the send-time scrub still
  // removes meeting codes.
  maskTextFn: maskReplayText,
  maskTextSelector: 'body',
  // Inside the /meet page ([data-meet-page]) images and `ph-no-capture`
  // elements are recorded; elsewhere both stay blocked, as before. Video,
  // canvas and iframes are blocked everywhere (a camera stream is never in
  // an rrweb recording anyway). The SDK's own blockClass would block
  // `ph-no-capture` on /meet too, so the selector takes that over.
  blockClass: 'ph-replay-block',
  blockSelector:
    'video, canvas, iframe, img:not([data-meet-page] img), .ph-no-capture:not([data-meet-page] .ph-no-capture)',
  maskAttributeFn: maskReplayAttribute,
  recordBody: false,
  recordHeaders: false,
  maskCapturedNetworkRequestFn: maskMeetReplayNetwork,
})

if (shouldEnable) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    // This app evaluates Call Guidance on the server and has no browser-side
    // PostHog flag consumers. SDK /flags requests include the initial URL as
    // person properties, which would disclose a /meet join credential.
    advanced_disable_feature_flags: true,
    // Recording starts only from code: hooks/use-app-replay on every page but
    // /meet (sampled), and components/meet/replay on a Call Guidance /meet
    // call (every one, overriding the sample).
    disable_session_recording: true,
    // A /meet call records unredacted under the PostHog BAA, with only the
    // credential scrub; every other page keeps protectMeetReplayEvent.
    before_send: protectMeetReplayEventSecretsOnly,
    capture_performance: false,
    // Console lines can carry page data, and no replay needs them.
    enable_recording_console_log: false,
    session_recording: isLocalReplay
      ? {
          ...sessionRecordingOptions({ production: true }),
          ...localReplayOverrides(),
        }
      : sessionRecordingOptions({ production: isProduction }),
  })
}

export const isPostHogEnabled = () => shouldEnable

/** PostHog is on only for local /meet replay (no app-wide replay). */
export const isLocalReplayMode = () => shouldEnable && isLocalReplay

export default posthog
